import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import {
  TrendingUp, Users, DollarSign, ShoppingCart, AlertTriangle, History,
  Wifi, ArrowUpRight, BarChart3, Settings, RefreshCw
} from 'lucide-react';
import useComercialComissao from 'features/comercial/hooks/useComercialComissao';
import useComissaoPresets from 'features/comercial/hooks/useComissaoPresets';
import SeletorPreset from 'features/comercial/components/comissao/SeletorPreset';
import PresetsAdmin from 'features/comercial/components/comissao/PresetsAdmin';
import { useAuth } from 'contexts/AuthContext';
import comissaoReguaService from 'features/comercial/services/comissaoReguaService';
import ComissaoReguaWorkspace, { mesCorrente, regimeDaCompetencia } from 'features/comercial/components/comissao/ComissaoReguaWorkspace';
import useCompanyContextKey from 'hooks/useCompanyContextKey';

const moeda = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inteiro = (n) => Number(n || 0).toLocaleString('pt-BR');
const iniciais = (nome) => (nome || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

/**
 * Último dia da competência, em data LOCAL.
 *
 * `new Date(ano, mes, 0)` já devolve o último dia no fuso do navegador; passar por `toISOString()`
 * converte para UTC e, em qualquer fuso positivo, a meia-noite local cai no dia anterior — o
 * período terminaria em 30 num mês de 31. No Brasil (UTC-3) o desvio anda para o outro lado e não
 * aparece, mas a correção é a mesma que a tela já aplica ao mês corrente.
 */
export const ultimoDiaDaCompetencia = (mes) => {
  const [ano, mesNumero] = String(mes).split('-').map(Number);
  const data = new Date(ano, mesNumero, 0);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
};

const SUB_ABAS = [
  { id: 'nao-instalados',  label: 'Clientes não instalados', key: 'clientes_nao_instalados' },
  { id: 'instalados',      label: 'Clientes instalados',     key: 'clientes_instalados' },
  { id: 'negociacoes',     label: 'Negociações',             key: 'detalhamento_negociacoes' },
  { id: 'ranking',         label: 'Ranking de vendas',       key: 'ranking_colaboradores' },
  { id: 'upsell',          label: 'Up Sells',                key: 'detalhamento_upsell' },
  { id: 'chip',            label: 'Chips',                   key: 'detalhamento_chip' },
  { id: 'inadimplentes',   label: 'Inadimplentes',           key: 'inadimplentes' },
];

/**
 * O que a tela mostra em cada regime de comissão.
 *
 * A partir de setembro/2026 a comissão conta a venda ASSINADA — instalada ou não — e não desconta
 * mais inadimplência. Quem diz qual regra valeu no período é a API, no campo `regime`
 * (`ativacao` | `assinatura` | `misto`); a tela não repete o corte, senão a data de virada passaria
 * a existir em dois lugares e um deles envelheceria sozinho.
 *
 * Duas decisões que valem por si:
 *
 * - **Esconder o desconto, e não zerá-lo.** Um "- R$ 0,00" fixo não se lê como "não desconta
 *   mais"; lê-se como "ninguém ficou devendo este mês". Período `misto` mantém o card, porque o
 *   pedaço legado dele tem estorno de verdade.
 * - **"Clientes instalados" vira "Vendas comissionadas".** A lista é a mesma; o que mudou é que ela
 *   deixou de ser de instalados. Manter o rótulo antigo seria afirmar na tela algo que o número
 *   desmente.
 */
export const leituraDoRegime = (regime) => {
  const porAssinatura = regime === 'assinatura';
  const temEstorno = !porAssinatura;

  return {
    porAssinatura,
    temEstorno,
    subAbas: (temEstorno ? SUB_ABAS : SUB_ABAS.filter(s => s.id !== 'inadimplentes'))
      .map(s => (porAssinatura && s.id === 'instalados' ? { ...s, label: 'Vendas comissionadas' } : s)),
  };
};

const StatCard = ({ icon: Icon, label, value, prefix, suffix, accent = false }) => (
  <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg p-4 shadow-sm">
    <div className="flex items-start justify-between gap-2 mb-3">
      <span className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] font-medium">{label}</span>
      {Icon && (
        <span className={`p-1.5 rounded-md ${accent ? 'bg-[color:var(--accent)]/10 text-[color:var(--accent)]' : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]'}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
    <div className={`text-2xl font-semibold tabular-nums ${accent ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'}`}>
      {prefix && <span className="text-sm text-[color:var(--text-muted)] font-normal mr-0.5">{prefix}</span>}
      {value}
      {suffix && <span className="text-sm text-[color:var(--text-muted)] font-normal ml-1">{suffix}</span>}
    </div>
  </div>
);

const LegacyComissaoTab = ({ filtroData }) => {
  const { user } = useAuth();
  const [filtros, setFiltros] = useState({
    vendedorKey: '',
    vendedor: '',
    colaborador: '',
    eh_loja: false,
    tipo: 'fixo',
    cidade: '',
  });
  const [subAba, setSubAba] = useState('nao-instalados');
  const [presetsAdminAberto, setPresetsAdminAberto] = useState(false);

  const { dados, loading, error, recarregar } = useComercialComissao(filtroData, filtros);
  const presetsCtl = useComissaoPresets();

  const podeEditarPresets = !!(user?.permissions || []).find(
    (p) => p?.name === 'comercial.metas.edit'
  );

  const k = dados.kpis || {};

  const dadosLinha = useMemo(
    () => (dados.ativacoes_tempo || []).map(d => ({ data: d.data, valor: Number(d.valor) || 0 })),
    [dados.ativacoes_tempo]
  );

  const dadosBarras = useMemo(
    () => (dados.top_cidades || []).map(d => ({ cidade: d.cidade, quantidade: Number(d.quantidade) || 0 })),
    [dados.top_cidades]
  );

  const handleFiltro = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const opcoes = dados.filtros || { vendedores: [], colaboradores: [], cidades: [] };

  const { porAssinatura, temEstorno, subAbas } = leituraDoRegime(dados.regime);
  const subAbaAtiva = subAbas.some(s => s.id === subAba) ? subAba : subAbas[0].id;

  const contagemSubAba = subAbas.reduce((acc, s) => {
    acc[s.id] = (dados[s.key] || []).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-[color:var(--surface-muted)] border border-[color:var(--status-warn)]/30 text-[color:var(--status-warn)] rounded-lg p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          <span>Falha ao carregar comissões: {error}</span>
          <button onClick={recarregar} className="ml-auto text-xs underline">Tentar novamente</button>
        </div>
      )}

      {/* Filtros locais */}
      <div className="flex flex-wrap items-center gap-2">
        <SeletorPreset
          presets={presetsCtl.presets}
          vendedoresN8n={opcoes.vendedores}
          value={filtros.vendedorKey}
          onChange={({ key, vendedor, colaborador, eh_loja, tipo }) => {
            setFiltros(prev => ({
              ...prev,
              vendedorKey: key,
              vendedor,
              colaborador,
              eh_loja,
              tipo: tipo || 'comercial',
            }));
          }}
        />
        <select
          className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md px-3 py-2 text-sm text-[color:var(--text-primary)]"
          value={filtros.cidade}
          onChange={(e) => handleFiltro('cidade', e.target.value)}
        >
          <option value="">Cidade — Todas</option>
          {opcoes.cidades.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        {podeEditarPresets && (
          <button
            type="button"
            onClick={() => setPresetsAdminAberto(true)}
            title="Gerenciar presets de vendedor/colaborador"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm border border-[color:var(--border)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
          >
            <Settings className="h-4 w-4" />
            Presets
          </button>
        )}
        {filtros.tipo === 'loja' && (
          <span className="self-center text-xs px-2 py-1 rounded-full bg-[color:var(--accent)]/10 text-[color:var(--accent)] font-medium">
            Loja · 10%
          </span>
        )}
        {filtros.tipo === 'fixo' && (
          <span className="self-center text-xs px-2 py-1 rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] font-medium border border-[color:var(--border)]">
            Fixo · R$15/venda
          </span>
        )}
        <button
          type="button"
          onClick={recarregar}
          disabled={loading}
          title="Forçar nova consulta ao n8n"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm border border-[color:var(--border)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Sincronizando…' : 'Sincronizar'}
        </button>
      </div>

      <PresetsAdmin
        aberto={presetsAdminAberto}
        onFechar={() => setPresetsAdminAberto(false)}
        presets={presetsCtl.presets}
        loading={presetsCtl.loading}
        error={presetsCtl.error}
        criar={presetsCtl.criar}
        atualizar={presetsCtl.atualizar}
        remover={presetsCtl.remover}
        vendedoresN8n={opcoes.vendedores}
        colaboradoresN8n={opcoes.colaboradores}
      />

      {/* Hero */}
      <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg p-6 shadow-sm grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] font-medium mb-3">
            Comissão líquida final
          </div>
          <div className="text-5xl font-semibold tracking-tight text-[color:var(--text-primary)] tabular-nums">
            <span className="text-2xl text-[color:var(--accent)] font-medium mr-2 align-top">R$</span>
            {moeda(k.comissao_liquida_final)}
          </div>
          <div className="mt-3 text-sm text-[color:var(--text-muted)]">
            {porAssinatura
              ? 'Apurado pelas vendas assinadas no período, instaladas ou não'
              : 'Apurado no período selecionado'}
          </div>
        </div>
        <div className={`grid gap-4 self-center ${temEstorno ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
          <div className="border-l border-[color:var(--border)] pl-4">
            <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] mb-1">Vendas</div>
            <div className="text-xl font-semibold text-[color:var(--text-primary)] tabular-nums">
              <span className="text-xs text-[color:var(--text-muted)] mr-0.5">R$</span>{moeda(k.comissao_vendas)}
            </div>
          </div>
          <div className="border-l border-[color:var(--border)] pl-4">
            <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] mb-1">Up Sell</div>
            <div className="text-xl font-semibold text-[color:var(--text-primary)] tabular-nums">
              <span className="text-xs text-[color:var(--text-muted)] mr-0.5">R$</span>{moeda(k.comissao_up_sell)}
            </div>
          </div>
          <div className="border-l border-[color:var(--border)] pl-4">
            <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] mb-1">Chip</div>
            <div className="text-xl font-semibold text-[color:var(--text-primary)] tabular-nums">
              <span className="text-xs text-[color:var(--text-muted)] mr-0.5">R$</span>{moeda(k.comissao_chip)}
            </div>
          </div>
          {temEstorno && (
            <div className="border-l border-[color:var(--border)] pl-4">
              <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] mb-1">Inadimplência</div>
              <div className="text-xl font-semibold text-[color:var(--status-warn)] tabular-nums">
                <span className="text-xs text-[color:var(--text-muted)] mr-0.5">- R$</span>{moeda(k.descontos_inadimplencia)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp}   label={porAssinatura ? 'Vendas no período' : 'Total ativações'} value={inteiro(k.total_ativacoes)} accent />
        <StatCard icon={DollarSign}   label="Ticket médio"    value={moeda(k.ticket_medio)} prefix="R$" />
        <StatCard icon={Users}        label="Novos clientes"  value={inteiro(k.novos_clientes)} />
        <StatCard icon={BarChart3}    label="Negociações"     value={inteiro(k.total_negociacoes)} />
        <StatCard icon={Wifi}         label="Chips vendidos"  value={inteiro(k.chips_vendidos)} />
        <StatCard icon={ArrowUpRight} label="Up Sells"        value={inteiro(k.qtd_up_sells)} />
        <StatCard icon={ShoppingCart} label="Receita total"   value={moeda(k.receita_total)} prefix="R$" />
        <StatCard icon={DollarSign}   label="Comissão (15%)"  value={moeda(k.comissao_vendas)} prefix="R$" accent />
      </div>

      {/* Sub-abas */}
      <div>
        <div className="flex flex-wrap gap-1 bg-[color:var(--surface-muted)] p-1 rounded-lg mb-4">
          {subAbas.map(s => (
            <button
              key={s.id}
              onClick={() => setSubAba(s.id)}
              className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex items-center gap-2 ${
                subAbaAtiva === s.id
                  ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                  : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
              }`}
            >
              {s.label}
              <span className="bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] px-2 py-0.5 rounded-full text-[10px] tabular-nums">
                {contagemSubAba[s.id] || 0}
              </span>
            </button>
          ))}
        </div>

        <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg overflow-hidden shadow-sm">
          {subAbaAtiva === 'nao-instalados' && <TabelaNaoInstalados linhas={dados.clientes_nao_instalados} />}
          {subAbaAtiva === 'instalados'     && <TabelaInstalados linhas={dados.clientes_instalados} />}
          {subAbaAtiva === 'negociacoes'    && <TabelaNegociacoes linhas={dados.detalhamento_negociacoes} />}
          {subAbaAtiva === 'ranking'        && <Ranking linhas={dados.ranking_colaboradores} tipoAtivo={filtros.tipo} presets={presetsCtl.presets} />}
          {subAbaAtiva === 'upsell'         && <TabelaUpsell linhas={dados.detalhamento_upsell} />}
          {subAbaAtiva === 'chip'           && <TabelaChips linhas={dados.detalhamento_chip} />}
          {subAbaAtiva === 'inadimplentes'  && <TabelaInadimplentes linhas={dados.inadimplentes} />}
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-[color:var(--text-primary)]">Ativações ao longo do tempo</h3>
            <span className="text-xs text-[color:var(--text-muted)]">Diário</span>
          </div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dadosLinha} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="data" stroke="var(--text-muted)" fontSize={11} />
                <YAxis stroke="var(--text-muted)" fontSize={11} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
                <Line type="monotone" dataKey="valor" stroke="var(--accent)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-[color:var(--text-primary)]">Top cidades</h3>
            <span className="text-xs text-[color:var(--text-muted)]">Por volume</span>
          </div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dadosBarras} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" stroke="var(--text-muted)" fontSize={11} />
                <YAxis dataKey="cidade" type="category" stroke="var(--text-muted)" fontSize={11} width={90} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
                <Bar dataKey="quantidade" fill="var(--accent)" radius={[0, 6, 6, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

const Avatar = ({ nome }) => (
  <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[color:var(--accent)]/15 text-[color:var(--accent)] text-xs font-semibold flex-shrink-0">
    {iniciais(nome)}
  </span>
);

const Empty = ({ colSpan, mensagem }) => (
  <tr><td colSpan={colSpan} className="text-center text-[color:var(--text-muted)] py-10 text-sm">{mensagem}</td></tr>
);

const baseTh = "text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider text-[color:var(--text-muted)] border-b border-[color:var(--border)]";
const baseTd = "px-4 py-3 text-sm text-[color:var(--text-primary)] border-b border-[color:var(--border)] last:border-b-0";

const TabelaNaoInstalados = ({ linhas = [] }) => (
  <div className="overflow-auto max-h-[460px]">
    <table className="w-full">
      <thead className="bg-[color:var(--surface)] sticky top-0 z-10">
        <tr>
          <th className={baseTh}>Cliente</th>
          <th className={baseTh}>E-mail</th>
          <th className={baseTh}>Cidade</th>
          <th className={`${baseTh} text-right`}>Contrato</th>
          <th className={baseTh}>Ativação</th>
          <th className={baseTh}>Vendedor</th>
          <th className={baseTh}>Login</th>
        </tr>
      </thead>
      <tbody>
        {linhas.length === 0 ? <Empty colSpan={7} mensagem="Tudo certo por aqui — nenhum cliente pendente" /> :
          linhas.map((r, i) => (
            <tr key={i} className="hover:bg-[color:var(--surface-muted)]/50">
              <td className={baseTd}>
                <div className="flex items-center gap-3"><Avatar nome={r.cliente} /><span>{r.cliente ?? ''}</span></div>
              </td>
              <td className={`${baseTd} text-[color:var(--text-muted)]`}>{r.email ?? ''}</td>
              <td className={baseTd}>
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--accent)]/10 text-[color:var(--accent)]">{r.cidade ?? ''}</span>
              </td>
              <td className={`${baseTd} text-right tabular-nums`}>{r.contrato_id ?? ''}</td>
              <td className={baseTd}>{r.data_ativacao ?? ''}</td>
              <td className={baseTd}>{r.vendedor_nome ?? ''}</td>
              <td className={baseTd}>
                <code className="text-xs bg-[color:var(--surface-muted)] px-2 py-0.5 rounded">{r.logins_sem_instalacao ?? ''}</code>
              </td>
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

const TIPO_LABEL = { comercial: 'Comercial 15%', loja: 'Loja 10%', fixo: 'Fixo R$15' };
const TIPO_BADGE = {
  comercial: 'bg-[color:var(--accent)]/10 text-[color:var(--accent)]',
  loja:      'bg-[color:var(--accent)]/10 text-[color:var(--accent)]',
  fixo:      'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]',
};

const Ranking = ({ linhas = [], tipoAtivo = 'fixo', presets = [] }) => {
  // mapa vendedor_nome (lower) → tipo do preset
  // o ranking retorna v.nome (vendedor) no campo "colaborador"
  const presetPorVendedor = useMemo(() => {
    const m = {};
    presets.filter(p => p.ativo !== false).forEach(p => {
      if (p.vendedor_nome) m[p.vendedor_nome.toLowerCase()] = p.tipo || 'comercial';
    });
    return m;
  }, [presets]);

  const tipoDeR = (r) => {
    // se há filtro de preset ativo, usa o tipo do filtro
    if (tipoAtivo !== 'fixo') return tipoAtivo;
    // senão busca pelo nome do vendedor (campo "colaborador" vindo do n8n)
    const key = (r.colaborador || '').toLowerCase();
    return presetPorVendedor[key] || 'fixo';
  };

  const comissaoDeR = (r) => {
    const tipo = tipoDeR(r);
    return (tipo === 'comercial' || tipo === 'loja')
      ? (r.comissao_total || 0)
      : (r.vendas || 0) * 15;
  };

  if (!linhas.length) return <div className="text-center text-[color:var(--text-muted)] py-10 text-sm">Sem dados de ranking no período</div>;

  const ord = [...linhas].sort((a, b) => comissaoDeR(b) - comissaoDeR(a));
  const max = Math.max(...ord.map(x => comissaoDeR(x))) || 1;

  return (
    <div className="divide-y divide-[color:var(--border)]">
      {ord.map((r, i) => {
        const tipo = tipoDeR(r);
        const total = comissaoDeR(r);
        const pct = (total / max) * 100;
        return (
          <div key={i} className="px-4 py-3 grid grid-cols-[40px_1fr_auto_auto] items-center gap-4">
            <span className="text-base font-bold text-[color:var(--text-muted)] tabular-nums text-center">{i + 1}</span>
            <div className="flex items-center gap-3 min-w-0">
              <Avatar nome={r.colaborador} />
              <div className="min-w-0">
                <div className="text-sm font-medium text-[color:var(--text-primary)] truncate">{r.colaborador ?? ''}</div>
                {r.cidade && <div className="text-xs text-[color:var(--text-muted)]">{r.cidade}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">Vendas</div>
              <div className="text-sm tabular-nums">{inteiro(r.vendas)}</div>
            </div>
            <div className="text-right min-w-[140px]">
              <span className={`text-[9px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-full ${TIPO_BADGE[tipo]}`}>
                {TIPO_LABEL[tipo]}
              </span>
              <div className="text-sm font-semibold text-[color:var(--accent)] tabular-nums mt-0.5">R$ {moeda(total)}</div>
              <div className="h-1 bg-[color:var(--surface-muted)] rounded mt-1 overflow-hidden">
                <div className="h-full bg-[color:var(--accent)] rounded" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TabelaUpsell = ({ linhas = [] }) => (
  <div className="overflow-auto max-h-[460px]">
    <table className="w-full">
      <thead className="bg-[color:var(--surface)] sticky top-0 z-10">
        <tr>
          <th className={`${baseTh} text-right`}>OS</th>
          <th className={`${baseTh} text-right`}>Protocolo</th>
          <th className={baseTh}>Cliente</th>
          <th className={baseTh}>CNPJ/CPF</th>
          <th className={baseTh}>Abertura</th>
          <th className={baseTh}>Fechamento</th>
          <th className={baseTh}>Tipo OS</th>
          <th className={`${baseTh} text-right`}>Comissão</th>
        </tr>
      </thead>
      <tbody>
        {linhas.length === 0 ? <Empty colSpan={8} mensagem="Sem up sells no período" /> :
          linhas.map((r, i) => (
            <tr key={i} className="hover:bg-[color:var(--surface-muted)]/50">
              <td className={`${baseTd} text-right tabular-nums`}>{r.os_id ?? ''}</td>
              <td className={`${baseTd} text-right tabular-nums`}>{r.protocolo ?? ''}</td>
              <td className={baseTd}>
                <div className="flex items-center gap-3"><Avatar nome={r.cliente} /><span>{r.cliente ?? ''}</span></div>
              </td>
              <td className={`${baseTd} text-[color:var(--text-muted)]`}>{r.cnpj_cpf ?? ''}</td>
              <td className={baseTd}>{r.data_abertura ?? ''}</td>
              <td className={baseTd}>{r.data_fechamento ?? ''}</td>
              <td className={baseTd}>
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--accent)]/10 text-[color:var(--accent)]">{r.tipo_os ?? ''}</span>
              </td>
              <td className={`${baseTd} text-right font-semibold text-[color:var(--accent)] tabular-nums`}>R$ {moeda(r.valor_comissao)}</td>
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

const TabelaChips = ({ linhas = [] }) => (
  <div className="overflow-auto max-h-[460px]">
    <table className="w-full">
      <thead className="bg-[color:var(--surface)] sticky top-0 z-10">
        <tr>
          <th className={`${baseTh} text-right`}>Cliente ID</th>
          <th className={baseTh}>Cliente</th>
          <th className={baseTh}>CPF/CNPJ</th>
          <th className={baseTh}>Plano</th>
          <th className={`${baseTh} text-right`}>Mensal</th>
          <th className={`${baseTh} text-right`}>Ativação</th>
          <th className={`${baseTh} text-right`}>Comissão</th>
          <th className={baseTh}>Data ativação</th>
          <th className={baseTh}>Cidade</th>
        </tr>
      </thead>
      <tbody>
        {linhas.length === 0 ? <Empty colSpan={9} mensagem="Sem chips vendidos no período" /> :
          linhas.map((r, i) => (
            <tr key={i} className="hover:bg-[color:var(--surface-muted)]/50">
              <td className={`${baseTd} text-right tabular-nums`}>{r.cliente_id ?? ''}</td>
              <td className={baseTd}>
                <div className="flex items-center gap-3"><Avatar nome={r.cliente} /><span>{r.cliente ?? ''}</span></div>
              </td>
              <td className={`${baseTd} text-[color:var(--text-muted)]`}>{r.cpf_cnpj ?? ''}</td>
              <td className={baseTd}>
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--accent)]/10 text-[color:var(--accent)]">{r.plano ?? ''}</span>
              </td>
              <td className={`${baseTd} text-right tabular-nums`}>R$ {moeda(r.valor_mensal)}</td>
              <td className={`${baseTd} text-right tabular-nums`}>R$ {moeda(r.valor_ativacao)}</td>
              <td className={`${baseTd} text-right font-semibold text-[color:var(--accent)] tabular-nums`}>R$ {moeda(r.comissao)}</td>
              <td className={baseTd}>{r.data_ativacao ?? ''}</td>
              <td className={baseTd}>{r.cidade ?? ''}</td>
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

const SITUACAO_BADGE = {
  'Instalado':        'bg-[color:var(--status-ok,#22c55e)]/15 text-[color:var(--status-ok,#22c55e)]',
  'Não instalado':    'bg-[color:var(--status-warn)]/15 text-[color:var(--status-warn)]',
  'Cancelado/Inativo':'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] border border-[color:var(--border)]',
};

const TabelaNegociacoes = ({ linhas = [] }) => {
  const [filtroSituacao, setFiltroSituacao] = React.useState('');
  const filtradas = filtroSituacao ? linhas.filter(r => r.situacao === filtroSituacao) : linhas;
  const contagens = {
    total:     linhas.length,
    instalado: linhas.filter(r => r.situacao === 'Instalado').length,
    nao:       linhas.filter(r => r.situacao === 'Não instalado').length,
    cancelado: linhas.filter(r => r.situacao === 'Cancelado/Inativo').length,
  };

  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--border)] flex-wrap">
        {[
          { key: '', label: 'Todos', count: contagens.total },
          { key: 'Instalado', label: 'Instalados', count: contagens.instalado },
          { key: 'Não instalado', label: 'Não instalados', count: contagens.nao },
          { key: 'Cancelado/Inativo', label: 'Cancelados/Inativos', count: contagens.cancelado },
        ].map(op => (
          <button
            key={op.key}
            onClick={() => setFiltroSituacao(op.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              filtroSituacao === op.key
                ? 'bg-[color:var(--accent)] text-white'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
            }`}
          >
            {op.label}
            <span className="bg-black/10 px-1.5 py-0.5 rounded-full tabular-nums">{op.count}</span>
          </button>
        ))}
      </div>
      <div className="overflow-auto max-h-[420px]">
        <table className="w-full">
          <thead className="bg-[color:var(--surface)] sticky top-0 z-10">
            <tr>
              <th className={baseTh}>Cliente</th>
              <th className={baseTh}>CPF/CNPJ</th>
              <th className={baseTh}>Vendedor</th>
              <th className={baseTh}>Plano</th>
              <th className={`${baseTh} text-right`}>Mensal</th>
              <th className={baseTh}>Negociação</th>
              <th className={baseTh}>Ativação</th>
              <th className={baseTh}>Cidade</th>
              <th className={baseTh}>Situação</th>
            </tr>
          </thead>
          <tbody>
            {filtradas.length === 0 ? <Empty colSpan={9} mensagem="Nenhuma negociação no período" /> :
              filtradas.map((r, i) => (
                <tr key={i} className="hover:bg-[color:var(--surface-muted)]/50">
                  <td className={baseTd}>
                    <div className="flex items-center gap-3"><Avatar nome={r.cliente} /><span>{r.cliente ?? ''}</span></div>
                  </td>
                  <td className={`${baseTd} text-[color:var(--text-muted)]`}>{r.cpf_cnpj ?? ''}</td>
                  <td className={baseTd}>{r.vendedor_nome ?? ''}</td>
                  <td className={baseTd}>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--accent)]/10 text-[color:var(--accent)]">{r.plano ?? ''}</span>
                  </td>
                  <td className={`${baseTd} text-right tabular-nums`}>R$ {moeda(r.valor_mensal)}</td>
                  <td className={baseTd}>{r.data_negociacao ?? ''}</td>
                  <td className={baseTd}>{r.data_ativacao ?? ''}</td>
                  <td className={baseTd}>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--accent)]/10 text-[color:var(--accent)]">{r.cidade ?? ''}</span>
                  </td>
                  <td className={baseTd}>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SITUACAO_BADGE[r.situacao] || ''}`}>
                      {r.situacao ?? ''}
                    </span>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TabelaInstalados = ({ linhas = [] }) => (
  <div className="overflow-auto max-h-[460px]">
    <table className="w-full">
      <thead className="bg-[color:var(--surface)] sticky top-0 z-10">
        <tr>
          <th className={`${baseTh} text-right`}>Cliente ID</th>
          <th className={baseTh}>Cliente</th>
          <th className={baseTh}>CPF/CNPJ</th>
          <th className={baseTh}>Vendedor</th>
          <th className={`${baseTh} text-right`}>Mensal</th>
          <th className={`${baseTh} text-right`}>Instalação</th>
          <th className={`${baseTh} text-right`}>Comissão</th>
          <th className={baseTh}>Data ativação</th>
          <th className={baseTh}>Cidade</th>
        </tr>
      </thead>
      <tbody>
        {linhas.length === 0 ? <Empty colSpan={9} mensagem="Nenhum cliente instalado no período" /> :
          linhas.map((r, i) => (
            <tr key={i} className="hover:bg-[color:var(--surface-muted)]/50">
              <td className={`${baseTd} text-right tabular-nums`}>{r.cliente_id ?? ''}</td>
              <td className={baseTd}>
                <div className="flex items-center gap-3"><Avatar nome={r.cliente} /><span>{r.cliente ?? ''}</span></div>
              </td>
              <td className={`${baseTd} text-[color:var(--text-muted)]`}>{r.cpf_cnpj ?? ''}</td>
              <td className={baseTd}>{r.vendedor_nome ?? ''}</td>
              <td className={`${baseTd} text-right tabular-nums`}>R$ {moeda(r.valor_mensal)}</td>
              <td className={`${baseTd} text-right tabular-nums`}>R$ {moeda(r.valor_instalacao)}</td>
              <td className={`${baseTd} text-right font-semibold text-[color:var(--accent)] tabular-nums`}>R$ {moeda(r.comissao)}</td>
              <td className={baseTd}>{r.data_ativacao ?? ''}</td>
              <td className={baseTd}>
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--accent)]/10 text-[color:var(--accent)]">{r.cidade ?? ''}</span>
              </td>
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

const TabelaInadimplentes = ({ linhas = [] }) => (
  <div className="overflow-auto max-h-[460px]">
    <table className="w-full">
      <thead className="bg-[color:var(--surface)] sticky top-0 z-10">
        <tr>
          <th className={baseTh}>Cliente</th>
          <th className={baseTh}>CPF/CNPJ</th>
          <th className={`${baseTh} text-right`}>Plano</th>
          <th className={baseTh}>Tipo contrato</th>
          <th className={`${baseTh} text-right`}>Desconto</th>
          <th className={baseTh}>1ª parcela vencida</th>
          <th className={`${baseTh} text-right`}>Atraso (dias)</th>
          <th className={`${baseTh} text-right`}>Parcelas</th>
        </tr>
      </thead>
      <tbody>
        {linhas.length === 0 ? <Empty colSpan={8} mensagem="Nenhum inadimplente no período" /> :
          linhas.map((r, i) => (
            <tr key={i} className="hover:bg-[color:var(--surface-muted)]/50">
              <td className={baseTd}>
                <div className="flex items-center gap-3"><Avatar nome={r.cliente} /><span>{r.cliente ?? ''}</span></div>
              </td>
              <td className={`${baseTd} text-[color:var(--text-muted)]`}>{r.cpf_cnpj ?? ''}</td>
              <td className={`${baseTd} text-right tabular-nums`}>R$ {moeda(r.valor_plano)}</td>
              <td className={baseTd}>{r.tipo_contrato ?? ''}</td>
              <td className={`${baseTd} text-right font-semibold text-[color:var(--status-warn)] tabular-nums`}>- R$ {moeda(r.valor_desconto)}</td>
              <td className={baseTd}>{r.primeira_parcela_vencida ?? ''}</td>
              <td className={`${baseTd} text-right tabular-nums`}>{r.maior_atraso ?? ''}</td>
              <td className={`${baseTd} text-right tabular-nums`}>{r.qtd_parcelas_atrasadas ?? ''}</td>
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

const ComissaoTab = ({ filtroData }) => {
  const { user } = useAuth();
  const [configuracao, setConfiguracao] = useState(null);
  const [checked, setChecked] = useState(false);
  // A competência vive AQUI, e não dentro do workspace: é ela que decide qual regime renderizar.
  const [mes, setMes] = useState(mesCorrente);

  // A vigência é POR EMPRESA, e trocar de empresa não remonta esta aba: sem esta dependência, quem
  // saísse de uma empresa com régua para outra sem régua continuaria vendo o workspace novo, ou o
  // inverso. `companyContext?.id` não serve — esse campo não existe no contexto.
  const empresa = useCompanyContextKey();

  useEffect(() => {
    let active = true;
    setChecked(false);
    comissaoReguaService.configuracao()
      .then((config) => { if (active) setConfiguracao(config); })
      // Empresas sem vigência, ou usuários em um contexto legado, permanecem no fluxo atual.
      .catch(() => { if (active) setConfiguracao(null); })
      .finally(() => { if (active) setChecked(true); });
    return () => { active = false; };
  }, [empresa]);

  if (!checked) return <div className="p-8 text-center text-sm text-[color:var(--text-muted)]">Carregando comissão…</div>;
  if (!configuracao && (user?.permissions || []).some((p) => p?.name === 'comercial.comissao.regra.edit')) {
    return <AtivarRegua onSaved={(config) => setConfiguracao(config)} />;
  }

  const inicio = configuracao?.inicio_vigencia ? String(configuracao.inicio_vigencia).slice(0, 7) : null;

  // Sem vigência cadastrada, nada muda: a empresa inteira segue no regime legado.
  if (!inicio) return <LegacyComissaoTab filtroData={filtroData} />;

  // A decisão é pela COMPETÊNCIA ESCOLHIDA, não pelo mês do navegador. Antes, escolher um mês
  // anterior à vigência mantinha o workspace montado recebendo 409 e exibindo zeros — o histórico
  // existia no regime legado e a tela não levava até ele.
  if (regimeDaCompetencia(mes, inicio) === 'regua') {
    return <ComissaoReguaWorkspace mes={mes} onMes={setMes} inicioVigencia={inicio}/>;
  }

  // Sem `filtroData`: o período aqui vem da competência escolhida, não do filtro global.
  return <HistoricoLegado mes={mes} onMes={setMes} inicioVigencia={inicio}/>;
};

/**
 * Competência anterior à vigência da régua, servida pelo regime legado.
 *
 * O seletor de mês é o MESMO do workspace, para a navegação entre regimes não ter degrau: quem
 * escolhe agosto vai para a apuração nova, quem escolhe julho continua vendo julho. O aviso fica
 * fixo porque as duas telas mostram números diferentes por construção — a régua tem meta e faixa
 * de atingimento; o legado tem taxa fixa —, e confundir as duas é o erro caro.
 */
const HistoricoLegado = ({ mes, onMes, inicioVigencia }) => {
  // O legado recorta por intervalo livre; a competência vira o mês fechado correspondente.
  const periodo = useMemo(() => ({
    start: `${mes}-01`,
    end: ultimoDiaDaCompetencia(mes),
  }), [mes]);

  return <div className="space-y-4">
    <section className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]"><History className="h-5 w-5"/></span>
        <div>
          <p className="text-xs uppercase tracking-[.16em] text-[color:var(--text-muted)]">Comercial · regime legado</p>
          <h2 className="mt-1 text-lg font-semibold text-[color:var(--text-primary)]">Histórico anterior à nova régua</h2>
          <p className="mt-1 text-sm text-[color:var(--text-muted)]">
            A nova régua vale a partir de <strong>{inicioVigencia.split('-').reverse().join('/')}</strong>. Competências anteriores são exibidas como foram apuradas na época — escolha {inicioVigencia.split('-').reverse().join('/')} ou depois para voltar à apuração nova.
          </p>
        </div>
      </div>
      <label className="grid gap-1.5">
        <span className="text-[11px] font-medium uppercase tracking-[.14em] text-[color:var(--text-muted)]">Competência</span>
        {/* SEM `max`: daqui se volta para a régua escolhendo a vigência ou qualquer mês depois. */}
        <input type="month" value={mes} onChange={(e) => onMes(e.target.value)} className="min-h-10 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 text-sm outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"/>
      </label>
    </section>
    <div className="flex items-start gap-2 rounded-lg border border-[color:var(--status-warning-border)] bg-[color:var(--status-warning-wash)] p-3 text-sm text-[color:var(--status-warning-fg)]">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0"/>
      <span>Os números abaixo <strong>não</strong> seguem a régua nova: a taxa é fixa, não há meta nem faixa de atingimento. Competências até <strong>agosto/2026</strong> também contam pela ativação instalada e descontam inadimplência — regra que mudou em setembro. Não compare diretamente com uma competência apurada.</span>
    </div>
    <LegacyComissaoTab filtroData={periodo}/>
  </div>;
};

const AtivarRegua = ({ onSaved }) => {
  // `mesCorrente()` e não `toISOString()`: às 21h de 31/08 em Santarém o UTC já é setembro, e tanto o
  // valor inicial quanto o `min` pulariam um mês — a vigência começaria depois do pretendido.
  const [mes, setMes] = useState(mesCorrente);
  const [erro, setErro] = useState(''); const [salvando, setSalvando] = useState(false);
  const salvar = async (event) => {
    event.preventDefault(); setSalvando(true); setErro('');
    try { onSaved(await comissaoReguaService.salvarConfiguracao(mes)); }
    catch (error) { setErro(error.response?.data?.message || 'Não foi possível definir a vigência.'); }
    finally { setSalvando(false); }
  };
  return <section className="max-w-2xl rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6 shadow-sm">
    <p className="text-xs uppercase tracking-[.16em] text-[color:var(--text-muted)]">Nova régua de comissão</p>
    <h2 className="mt-2 text-xl font-semibold">Defina a competência de início</h2>
    <p className="mt-2 text-sm text-[color:var(--text-muted)]">Meses anteriores continuam no histórico legado. Metas, tickets e faixas do primeiro mês serão informados manualmente depois desta ativação.</p>
    <form onSubmit={salvar} className="mt-5 flex flex-wrap items-end gap-3"><label className="grid gap-1 text-sm font-medium">Início de vigência<input required type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2"/><span className="max-w-sm text-xs font-normal text-[color:var(--text-muted)]">No primeiro cadastro, pode ser uma competência já iniciada para preservar o corte acordado.</span></label><button disabled={salvando} className="rounded-md bg-[color:var(--accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{salvando ? 'Salvando…' : 'Ativar nova régua'}</button></form>
    {erro && <p className="mt-3 text-sm text-[color:var(--status-warn)]">{erro}</p>}
  </section>;
};

export default ComissaoTab;
